import PropTypes from "prop-types";
import React, { lazy, Suspense, useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import { Button, Checkbox, ChevronDown } from "@opendate-app/setlist";
import RefreshCwIcon from "../../shared/Icons/RefreshCwIcon";
import CopyOffersPicker from "../../shared/components/CopyOffersPicker";
import SeriesNavIcon from "../../shared/Icons/SeriesNavIcon";
import MakeRecurringModal from "../../ConfirmShow/components/MakeRecurringModal";
import CheckoutEmbedModal from "../../shared/CheckoutEmbedModal/CheckoutEmbedModal";
import HoldRemoveModal from "./actions/HoldRemoveModal";
import HoldConfirmModal from "./actions/HoldConfirmModal";
import UnpastModal from "./actions/UnpastModal";
import { toastError, errorText, EVENT_REMOVE_ERROR } from "../../../shared/toastHelper";

// Lazy so the classic popups' heavy deps (Tagify, react-iframe, draft-js) only
// load when the user opens Send Show Advance or Share this Page.
const ShowAdvanceSend = lazy(() => import("./actions/ShowAdvanceSend"));
const SharePage = lazy(() => import("./actions/SharePage"));

const menuContentStyle = {
  border: "1px solid var(--sl-border-secondary)",
  borderRadius: "8px",
  boxShadow: "0 4px 6px -2px rgba(10,13,18,0.03), 0 12px 16px -4px rgba(10,13,18,0.08)",
  width: "260px",
  padding: "4px",
  background: "var(--sl-bg-primary)",
  // Above the sticky tab strip (z-index: 100 in eventPageStyles.css).
  zIndex: 9999,
};

const modalContentStyle = {
  border: "none",
  borderRadius: "12px",
  boxShadow: "0 8px 8px -4px rgba(10,13,18,0.03), 0 20px 24px -4px rgba(10,13,18,0.08)",
  width: "min(440px, calc(100vw - 32px))",
  padding: "24px",
  background: "var(--sl-bg-primary)",
};

// Rows that open a confirm dialog before firing — destructive ones (Cancel/
// Uncancel/Remove) plus non-destructive Duplicate (confirmClass overrides the
// button color).
const ConfirmItem = ({ icon, label, danger, title, body, confirmLabel, confirmClass, onConfirm, closeMenu, cancelLabel, onOpen, children }) => (
  <Popup
    modal
    closeOnDocumentClick
    contentStyle={modalContentStyle}
    onOpen={(e) => { e.preventDefault(); if (onOpen) onOpen(); }}
    trigger={
      <button type="button" className={`event-actions-item${danger ? " is-danger" : ""}`}>
        <span className="event-actions-item-icon">{icon}</span>
        {label}
      </button>
    }
  >
    {(closeDialog) => (
      <div className="event-actions-dialog">
        <h5 className="event-actions-dialog-title">{title}</h5>
        <p className="event-actions-dialog-body">{body}</p>
        {children}
        <div className="event-actions-dialog-buttons">
          <button type="button" className="event-actions-btn-secondary" onClick={() => closeDialog()}>
            {cancelLabel || "Never mind"}
          </button>
          <button
            type="button"
            className={confirmClass || "event-actions-btn-danger"}
            onClick={() => {
              closeDialog();
              if (closeMenu) closeMenu();
              onConfirm();
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    )}
  </Popup>
);

ConfirmItem.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  danger: PropTypes.bool,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  confirmClass: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  closeMenu: PropTypes.func,
  cancelLabel: PropTypes.string,
  onOpen: PropTypes.func,
  children: PropTypes.node,
};

const todayInputValue = (timeZone) =>
  new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

const DuplicateWithDateItem = ({ timeZone, copyableOffers = [], onConfirm, closeMenu }) => {
  const [date, setDate] = useState("");
  const [copyOfferIds, setCopyOfferIds] = useState(copyableOffers.map((offer) => offer.id));
  const minDate = todayInputValue(timeZone);
  const invalidDate = !date || date < minDate;
  return (
    <Popup
      modal
      closeOnDocumentClick
      contentStyle={modalContentStyle}
      onOpen={(e) => e.preventDefault()}
      trigger={
        <button type="button" className="event-actions-item">
          <span className="event-actions-item-icon"><i className="fas fa-copy" /></span>
          Duplicate Event
        </button>
      }
    >
      {(closeDialog) => (
        <div className="event-actions-dialog">
          <h5 className="event-actions-dialog-title">Duplicate this event?</h5>
          <p className="event-actions-dialog-body">
            Pick a date for the copy — its show details will shift to that day.
          </p>
          <input
            type="date"
            className="form-control"
            min={minDate}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ marginBottom: "16px" }}
          />
          {copyableOffers.length > 0 && (
            <div className="mb-3">
              <CopyOffersPicker offers={copyableOffers} selectedIds={copyOfferIds} onChange={setCopyOfferIds} />
            </div>
          )}
          <div className="event-actions-dialog-buttons">
            <button type="button" className="event-actions-btn-secondary" onClick={() => closeDialog()}>
              Cancel
            </button>
            <button
              type="button"
              className="event-actions-btn-primary"
              disabled={invalidDate}
              onClick={() => {
                closeDialog();
                if (closeMenu) closeMenu();
                onConfirm(date, copyOfferIds);
              }}
            >
              Duplicate
            </button>
          </div>
        </div>
      )}
    </Popup>
  );
};

DuplicateWithDateItem.propTypes = {
  timeZone: PropTypes.string,
  copyableOffers: PropTypes.array,
  onConfirm: PropTypes.func.isRequired,
  closeMenu: PropTypes.func,
};

const ActionsMenu = ({ teamId, confirm, currentUser, csrfToken, actionDataUrl, duplicateUrl, holdActionsUrl }) => {
  const [busy, setBusy] = useState(false);
  const [makeRecurringOpen, setMakeRecurringOpen] = useState(false);
  const [holdData, setHoldData] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionData, setActionData] = useState(null);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [pauseSales, setPauseSales] = useState(true);
  const [makePrivate, setMakePrivate] = useState(true);
  const [resumeSales, setResumeSales] = useState(true);
  const [makePublic, setMakePublic] = useState(true);
  const [unpastOpen, setUnpastOpen] = useState(false);
  const perms = currentUser || {};
  const base = `/teams/${teamId}/confirms/${confirm.id}`;
  const canceled = confirm.calendarClassification === "canceled";
  const postponed = confirm.calendarClassification === "postponed";
  const isConfirm = confirm.type === "Confirm";
  const isHold = confirm.type === "Hold";

  const loadHoldData = () => {
    if (holdData) return Promise.resolve(holdData);
    return axios.get(holdActionsUrl).then((r) => {
      setHoldData(r.data);
      return r.data;
    }).catch(() => { toastError("Unable to load hold actions. Please try again."); });
  };

  // Seed data for the Share / Show Advance popups — fetched once on first use
  // and cached, mirroring loadHoldData.
  const loadActionData = () => {
    if (actionData) return Promise.resolve(actionData);
    return axios.get(actionDataUrl).then((r) => {
      setActionData(r.data);
      return r.data;
    }).catch(() => { toastError("Unable to load. Please try again."); });
  };

  // MakeRecurringModal reads snake_case fields; the EventPage confirm prop is camelCase.
  const recurringConfirm = {
    id: confirm.id,
    title: confirm.title,
    start_time: confirm.startTime,
    event_series: confirm.eventSeries || [],
    one_recurring_series: confirm.oneRecurringSeries,
    make_recurring_blocked_reason: confirm.makeRecurringBlockedReason,
  };

  const run = (request, onSuccess, errorFallback) => {
    if (busy) return;
    setBusy(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    request()
      .then(onSuccess)
      .catch((e) => toastError(errorText(e, errorFallback)))
      .finally(() => setBusy(false));
  };

  const reload = () => window.location.reload();

  const duplicateEvent = (extraParams = {}) =>
    run(
      () => axios.post(duplicateUrl, { confirm: { title: `${confirm.title} (Copy)`, ...extraParams } }),
      ({ data }) => { window.location.href = `/teams/${teamId}/confirms/${data.id}`; }
    );

  return (
    <>
    <Popup
      arrow={false}
      offsetY={5}
      position="bottom right"
      contentStyle={menuContentStyle}
      onOpen={(e) => { e.stopPropagation(); e.preventDefault(); }}
      trigger={
        <span>
          <Button variant="primary" trailingIcon={ChevronDown} disabled={busy}>Actions</Button>
        </span>
      }
    >
      {(close) => (
        <div className="event-actions-menu">
          {isHold ? (
            <>
              {perms.canCreateCalendarEvent && (
                <button
                  type="button"
                  className="event-actions-item"
                  onClick={() => { close(); loadHoldData().then(() => setConfirmOpen(true)); }}
                >
                  <span className="event-actions-item-icon"><i className="far fa-calendar-check" /></span>
                  Confirm this event
                </button>
              )}
              {perms.canDestroyCalendarEvent && (
                <button
                  type="button"
                  className="event-actions-item"
                  onClick={() => { close(); loadHoldData().then(() => setRemoveOpen(true)); }}
                >
                  <span className="event-actions-item-icon"><i className="far fa-calendar-plus" /></span>
                  Remove from calendar
                </button>
              )}
            </>
          ) : (
          <>
          {perms.canCreateInvitation && (
            <button
              type="button"
              className="event-actions-item"
              onClick={() => { close(); loadActionData().then((d) => { if (d) setShareOpen(true); }); }}
            >
              <span className="event-actions-item-icon"><i className="fas fa-share" /></span>
              Share this Page
            </button>
          )}
          {perms.canCreateShowAdvanceSend && (
            <button
              type="button"
              className="event-actions-item"
              onClick={() => { close(); loadActionData().then((d) => { if (d) setAdvanceOpen("email"); }); }}
            >
              <span className="event-actions-item-icon"><i className="fas fa-paper-plane" /></span>
              Send Show Advance
            </button>
          )}
          <button
            type="button"
            className="event-actions-item"
            onClick={() => { close(); loadActionData().then((d) => { if (d) setAdvanceOpen("pdf"); }); }}
          >
            <span className="event-actions-item-icon"><i className="fas fa-print" /></span>
            Print Show Advance
          </button>

          <a href={`${base}/confirmation_memo`} className="event-actions-item">
            <span className="event-actions-item-icon"><i className="fas fa-file-alt" /></span>
            Confirmation Memo
          </a>
          <a href={`${base}/event_qr_code`} className="event-actions-item">
            <span className="event-actions-item-icon"><i className="fas fa-qrcode" /></span>
            Event QR Code
          </a>
          {isConfirm && confirm.published && (
            <a href={`${base}/ticket_audit`} className="event-actions-item">
              <span className="event-actions-item-icon"><i className="far fa-clipboard-list" /></span>
              View Ticket Audit
            </a>
          )}
          {isConfirm && confirm.published && (
            <button
              type="button"
              className="event-actions-item"
              onClick={() => { close(); setEmbedOpen(true); }}
            >
              <span className="event-actions-item-icon"><i className="far fa-code" /></span>
              Embed Widget
            </button>
          )}

          {isConfirm && perms.canCreateCalendarEvent && (
            <a href={`/teams/${teamId}/event_series`} className="event-actions-item">
              <span className="event-actions-item-icon"><SeriesNavIcon className="" size={16} /></span>
              Add Event to Series
            </a>
          )}
          {isConfirm && perms.canCreateCalendarEvent && (
            <DuplicateWithDateItem
              timeZone={confirm.timeZone}
              copyableOffers={confirm.copyableOffers}
              closeMenu={close}
              onConfirm={(startDate, copyOfferIds) => duplicateEvent({ start_date: startDate, copy_offer_ids: copyOfferIds })}
            />
          )}
          {isConfirm && perms.canCreateCalendarEvent && (
            <button
              type="button"
              className="event-actions-item"
              onClick={() => { close(); setMakeRecurringOpen(true); }}
            >
              <span className="event-actions-item-icon"><RefreshCwIcon width="14" height="14" /></span>
              Make this a recurring event
            </button>
          )}

          {!postponed && perms.canCancelCalendarEvent && confirm.mayPostpone && (
            <ConfirmItem
              icon={<i className="far fa-clock" />}
              label="Postpone Event"
              title="Postpone this event?"
              body="The event keeps its current date on your calendar and stops counting as a past event, so you can move it once a new date is set. Ticket sales are not paid out while it is postponed."
              confirmLabel="Yes, postpone"
              closeMenu={close}
              onOpen={() => { setPauseSales(true); setMakePrivate(true); }}
              onConfirm={() => run(() => axios.post(`${base}/postpone`, { confirm: { pause_sales: pauseSales, make_private: makePrivate } }), reload)}
            >
              <div className="mb-3">
                <Checkbox
                  id="postpone-pause-sales"
                  label="Pause ticket sales"
                  checked={pauseSales}
                  onChange={(e) => setPauseSales(e.target.checked)}
                />
                <Checkbox
                  id="postpone-make-private"
                  label="Make event private"
                  checked={makePrivate}
                  onChange={(e) => setMakePrivate(e.target.checked)}
                />
              </div>
            </ConfirmItem>
          )}
          {postponed && perms.canCancelCalendarEvent && confirm.mayUnpostpone && (
            <ConfirmItem
              icon={<i className="far fa-calendar-check" />}
              label="Un-postpone Event"
              title="Un-postpone this event?"
              body="The event goes back to the status its dates call for. Update the date first if it has already gone by."
              confirmLabel="Yes, un-postpone"
              confirmClass="event-actions-btn-primary"
              closeMenu={close}
              onOpen={() => { setResumeSales(true); setMakePublic(true); }}
              onConfirm={() => run(() => axios.post(`${base}/unpostpone`, { confirm: { resume_sales: resumeSales, make_public: makePublic } }), reload)}
            >
              <div className="mb-3">
                <Checkbox
                  id="unpostpone-resume-sales"
                  label="Resume ticket sales"
                  checked={resumeSales}
                  onChange={(e) => setResumeSales(e.target.checked)}
                />
                <Checkbox
                  id="unpostpone-make-public"
                  label="Make event public"
                  checked={makePublic}
                  onChange={(e) => setMakePublic(e.target.checked)}
                />
              </div>
            </ConfirmItem>
          )}
          {!canceled && perms.canCancelCalendarEvent && confirm.mayCancel && (
            <ConfirmItem
              icon={<i className="far fa-calendar-times" />}
              label="Cancel Event"
              title="Cancel this event?"
              body="Are you sure you want to cancel this event in Opendate?"
              confirmLabel="Yes, cancel"
              closeMenu={close}
              onConfirm={() => run(() => axios.post(`${base}/cancel`), reload)}
            />
          )}
          {canceled && perms.canCancelCalendarEvent && confirm.mayUncancel && (
            <ConfirmItem
              icon={<i className="far fa-calendar" />}
              label="Uncancel Event"
              title="Uncancel this event?"
              body="Are you sure you want to uncancel this event?"
              confirmLabel="Yes, uncancel"
              closeMenu={close}
              onConfirm={() => run(() => axios.post(`${base}/uncancel`), reload)}
            />
          )}
          {perms.canUnpastCalendarEvent && confirm.mayUnpast && (
            <button
              type="button"
              className="event-actions-item"
              onClick={() => { close(); setUnpastOpen(true); }}
            >
              <span className="event-actions-item-icon"><i className="far fa-calendar-check" /></span>
              Move out of Past
            </button>
          )}
          {perms.canDestroyCalendarEvent && (
            <ConfirmItem
              icon={<i className="fas fa-trash" />}
              label="Remove"
              danger
              title="Remove event?"
              body="Remove this event from your Opendate account?"
              confirmLabel="Remove"
              closeMenu={close}
              onConfirm={() =>
                run(
                  () => axios.delete(`/teams/${teamId}/calendar_events/${confirm.id}`),
                  () => { window.location.href = "/dashboard"; },
                  EVENT_REMOVE_ERROR
                )
              }
            />
          )}
          </>
          )}
        </div>
      )}
    </Popup>
    {confirm.mayUnpast && (
      <UnpastModal
        open={unpastOpen}
        onClose={() => setUnpastOpen(false)}
        teamId={teamId}
        confirm={confirm}
        blockers={confirm.unpastBlockers}
        sideEffects={confirm.postEventSideEffects}
        csrfToken={csrfToken}
        onRestored={reload}
      />
    )}
    <MakeRecurringModal
      open={makeRecurringOpen}
      confirm={recurringConfirm}
      team={{ id: teamId }}
      onClose={() => setMakeRecurringOpen(false)}
      onCreated={(series) => { window.location = `/teams/${teamId}/event_series/${series.id}`; }}
    />
    {embedOpen && (
      <CheckoutEmbedModal
        open
        onClose={() => setEmbedOpen(false)}
        iframeId={`od-confirm-${confirm.id}-iframe`}
        title={confirm.title}
        embedUrl={confirm.widgetEmbedUrl}
      />
    )}
    {isHold && holdData && (
      <>
        <HoldRemoveModal
          open={removeOpen}
          teamId={teamId}
          holdId={confirm.id}
          csrfToken={csrfToken}
          hasArtists={holdData.hasArtists}
          reasonRequired={holdData.requireRemovalReason}
          onClose={() => setRemoveOpen(false)}
        />
        <HoldConfirmModal
          open={confirmOpen}
          teamId={teamId}
          holdId={confirm.id}
          csrfToken={csrfToken}
          holdPositions={holdData.holdPositions}
          rooms={holdData.rooms}
          roomIds={holdData.roomIds}
          venueOwnerships={holdData.venueOwnerships}
          eventTemplate={holdData.eventTemplate}
          typedDateEntryEnabled={holdData.typedDateEntryEnabled}
          doorsTimeDefault={holdData.doorsTimeDefault}
          eventEndTimeDefault={holdData.eventEndTimeDefault}
          eventTimeDefaults={holdData.eventTimeDefaults}
          onClose={() => setConfirmOpen(false)}
        />
      </>
    )}
    {actionData && (
      <Suspense fallback={null}>
        <ShowAdvanceSend
          open={advanceOpen}
          onClose={() => setAdvanceOpen(false)}
          teamId={teamId}
          csrfToken={csrfToken}
          actionData={actionData}
        />
        <SharePage
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          teamId={teamId}
          csrfToken={csrfToken}
          actionData={actionData}
        />
      </Suspense>
    )}
    </>
  );
};

ActionsMenu.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirm: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    type: PropTypes.string,
    published: PropTypes.bool,
    calendarClassification: PropTypes.string,
    mayCancel: PropTypes.bool,
    mayUncancel: PropTypes.bool,
    mayPostpone: PropTypes.bool,
    mayUnpostpone: PropTypes.bool,
    mayUnpast: PropTypes.bool,
    unpastBlockers: PropTypes.arrayOf(PropTypes.string),
    postEventSideEffects: PropTypes.object,
    title: PropTypes.string,
    startTime: PropTypes.string,
    timeZone: PropTypes.string,
    eventSeries: PropTypes.array,
    widgetEmbedUrl: PropTypes.string,
    copyableOffers: PropTypes.array,
  }).isRequired,
  currentUser: PropTypes.object,
  csrfToken: PropTypes.string,
  actionDataUrl: PropTypes.string.isRequired,
  duplicateUrl: PropTypes.string.isRequired,
  holdActionsUrl: PropTypes.string,
};

export default ActionsMenu;
